/// Port of mobile/lib/core/ui/pixel/pixel_volume_slider.dart.

import { useRef } from 'react';

import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from './PixelContainer';

const trackHeight = 28;
const thumbSize = 18;
const percentWidth = 44;

export interface PixelVolumeSliderProps {
  value: number;
  onChange: (value: number) => void;
  enabled?: boolean;
  pixelScale?: number;
  radius?: number;
}

export function PixelVolumeSlider({
  value,
  onChange,
  enabled = true,
  pixelScale = 2,
  radius = 3,
}: PixelVolumeSliderProps) {
  const colors = useThemeColors();
  const clamped = Math.min(1, Math.max(0, value));
  const percent = Math.round(clamped * 100);

  return (
    <div style={{ opacity: enabled ? 1 : 0.55 }}>
      <PixelContainer
        radius={radius}
        pixelScale={pixelScale}
        padding="8px 12px"
        backgroundColor={colors.surfaceContainerHighest}
        borderColor={colors.onSurface}
      >
        <div className="flex items-center gap-3">
          <PixelTrack
            value={clamped}
            enabled={enabled}
            pixelScale={pixelScale}
            onChange={onChange}
          />
          <span
            className="shrink-0 text-right leading-none"
            style={{
              width: percentWidth,
              fontSize: 20,
              color: colors.onSurface,
              opacity: enabled ? 1 : 0.4,
            }}
          >
            {percent}%
          </span>
        </div>
      </PixelContainer>
    </div>
  );
}

function PixelTrack({
  value,
  enabled,
  pixelScale,
  onChange,
}: {
  value: number;
  enabled: boolean;
  pixelScale: number;
  onChange: (value: number) => void;
}) {
  const colors = useThemeColors();
  const innerWidthRef = useRef(0);

  const updateFromDx = (localDx: number): void => {
    if (!enabled) return;
    const innerWidth = innerWidthRef.current;
    if (innerWidth <= 0) return;
    const usable = innerWidth - thumbSize;
    if (usable <= 0) return;
    const raw = Math.min(1, Math.max(0, (localDx - thumbSize / 2) / usable));
    onChange(Math.round(raw * 100) / 100);
  };

  const usable = Math.max(0, innerWidthRef.current - thumbSize);
  const thumbLeft = usable * value;

  return (
    <div className="min-w-0 flex-1" style={{ height: trackHeight }}>
      <PixelContainer
        radius={2}
        pixelScale={pixelScale}
        padding="2px"
        backgroundColor={colors.surfaceContainerLow}
        borderColor={colors.onSurface}
        className="h-full"
      >
        <div
          className="relative h-full overflow-hidden"
          onPointerDown={(event) => {
            if (!enabled) return;
            const rect = event.currentTarget.getBoundingClientRect();
            innerWidthRef.current = rect.width;
            updateFromDx(event.clientX - rect.left);
          }}
          onPointerMove={(event) => {
            if (!enabled || event.buttons === 0) return;
            const rect = event.currentTarget.getBoundingClientRect();
            innerWidthRef.current = rect.width;
            updateFromDx(event.clientX - rect.left);
          }}
          ref={(node) => {
            if (node) innerWidthRef.current = node.clientWidth;
          }}
        >
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: Math.min(innerWidthRef.current, thumbLeft + thumbSize / 2),
              backgroundColor: colors.onSurface,
            }}
          />
          <div
            className="absolute inset-y-0 flex items-center"
            style={{ left: thumbLeft }}
          >
            <PixelContainer
              radius={1}
              pixelScale={pixelScale}
              padding="0"
              backgroundColor={colors.surfaceContainerHighest}
              borderColor={colors.onSurface}
              style={{ width: thumbSize, height: thumbSize }}
            >
              <span className="block" style={{ width: thumbSize, height: thumbSize }} />
            </PixelContainer>
          </div>
        </div>
      </PixelContainer>
    </div>
  );
}
